import { cva } from 'class-variance-authority'
import { Loader2 } from 'lucide-react'
import { cn } from '../lib/utils'

// Espelha o Button do apps/portal (design system Glass v3).
const buttonVariants = cva(
  [
    'inline-flex items-center justify-center gap-2 font-semibold',
    'transition-all duration-300 cursor-pointer',
    'disabled:pointer-events-none disabled:opacity-50',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
  ],
  {
    variants: {
      variant: {
        primary: [
          'bg-primary text-primary-foreground rounded-full',
          'shadow-[0_2px_8px_rgba(255,77,0,0.2)]',
          'hover:shadow-[0_8px_28px_-4px_rgba(255,77,0,0.35)]',
          'hover:-translate-y-0.5 hover:scale-[1.02]',
          'active:scale-[0.98]',
        ],
        secondary: [
          'bg-card text-card-foreground border border-border rounded-full',
          'shadow-sm hover:bg-surface-2 hover:shadow-md hover:-translate-y-px',
        ],
        ghost: ['bg-transparent text-muted-foreground hover:text-foreground'],
      },
      size: {
        md: 'h-11 px-6 text-sm',
        lg: 'h-13 px-8 text-base',
      },
    },
    defaultVariants: { variant: 'primary', size: 'lg' },
  },
)

export function Button({ className, variant, size, loading, children, disabled, ...props }) {
  return (
    <button
      type="button"
      className={cn(buttonVariants({ variant, size, className }))}
      disabled={disabled || loading}
      {...props}
    >
      {loading && <Loader2 className="h-4 w-4 animate-spin" />}
      {children}
    </button>
  )
}

export function Card({ className, children, ...props }) {
  return (
    <div className={cn('glass rounded-3xl p-8 md:p-10', className)} {...props}>
      {children}
    </div>
  )
}

export function ProgressBar({ value = 0, max = 100, className }) {
  const pct = Math.min(Math.max((value / max) * 100, 0), 100)
  return (
    <div className={cn('h-1.5 w-full overflow-hidden rounded-full bg-muted', className)}>
      <div
        className="h-full rounded-full bg-primary transition-all duration-500 ease-out"
        style={{ width: `${pct}%` }}
      />
    </div>
  )
}

export function Field({ label, children, hint }) {
  return (
    <label className="block space-y-1.5">
      <span className="text-sm font-semibold text-foreground">{label}</span>
      {children}
      {hint && <span className="block text-xs text-muted-foreground">{hint}</span>}
    </label>
  )
}

export function Input({ className, ...props }) {
  return (
    <input
      className={cn(
        'h-11 w-full rounded-xl border border-border bg-surface px-4 text-sm text-foreground',
        'placeholder:text-text-tertiary transition-all',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        className,
      )}
      {...props}
    />
  )
}

export function Select({ className, children, ...props }) {
  return (
    <select
      className={cn(
        'h-11 w-full rounded-xl border border-border bg-surface px-4 text-sm text-foreground',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        className,
      )}
      {...props}
    >
      {children}
    </select>
  )
}
